import { toastr } from 'react-redux-toastr';
import { EstimationApi, UploadedFileData } from '../interfaces/api';
import EstimationRequestPost from '../interfaces/estimationRequestPost';
import API_SERVICE from '../../api/api.service';
import appRequest from '../../utils/request';
import { unexpectedServerError } from '../../utils/network-error-msg';

async function startUpload(filename: string, filetype: string, clientName: string, creationTime: number): Promise<{ uploadId: string; key: string }> {
  const response = await appRequest(`estimation/image/upload/start/${clientName}/${creationTime}`, {
    method: 'POST',
    data: JSON.stringify({ filename, filetype }),
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response || !response.data) throw new Error('Failed to start upload');
  return response.data;
}

async function uploadChunk(uploadId: string, partNumber: number, key: string, fileChunk: Blob): Promise<{ ETag: string; PartNumber: number }> {
  const formData = new FormData();
  formData.append('uploadId', uploadId);
  formData.append('partNumber', partNumber.toString());
  formData.append('key', key);
  formData.append('file', fileChunk);
  const response = await appRequest('estimation/image/upload/chunk', {
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response || !response.data) throw new Error('Failed to upload chunk');
  return response.data;
}

async function completeUpload(uploadId: string): Promise<{ message: string; url: string }> {
  const response = await appRequest('estimation/image/upload/complete', {
    method: 'POST',
    data: JSON.stringify({ uploadId }),
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response || !response.data) throw new Error('Failed to complete upload');
  return response.data;
}

async function createRequest(requestBody: EstimationRequestPost): Promise<void> {
  const response = await appRequest(`estimation/new`, {
    method: 'POST',
    data: { requestBody },
  });

  if (!response) {
    unexpectedServerError();
    return;
  }

  const { status, data: errorResponse } = response;

  if (status !== 201) {
    return toastr.error('Server error', "Can't create estimation. Try again later.");
  }

  if (errorResponse) {
    return toastr.error('Error', errorResponse);
  }
}

async function uploadImage(clientName: string, creationTime: number, file: UploadedFileData): Promise<void> {
  await API_SERVICE.estimateController.uploadImage.call({
    requestBody: file,
    parameters: { clientName, creationTime },
  });
}

async function uploadImageOrder(clientName: string, creationTime: number, file: UploadedFileData): Promise<void> {
  await API_SERVICE.cartController.uploadOrderImage.call({
    requestBody: file,
    parameters: { clientName, creationTime },
  });
}

async function abortUpload(uploadId: string, key: string): Promise<void> {
  const response = await appRequest('estimation/image/upload/abort', {
    method: 'POST',
    data: JSON.stringify({ uploadId, key }),
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response || response.status !== 200) throw new Error('Failed to abort upload');
}

/**
 * Estimation API definitions for production use.
 *
 * These definitions can only be covered by end-to-end (e2e) tests.
 */
const estimationApiProd: EstimationApi = {
  createRequest,
  uploadImage,
  uploadImageOrder,
  startUpload,
  uploadChunk,
  completeUpload,
  abortUpload,
};

export default estimationApiProd;
